import { defineStore } from 'pinia'

import bgLoopMp4 from '@/assets/videos/camelrace/bgLoop.mp4'
import gatesWebm from '@/assets/videos/camelrace/gates.webm'
import gatesMov from '@/assets/videos/camelrace/gates.mov'
import greenWebm from '@/assets/videos/camelrace/green.webm'
import greenMov from '@/assets/videos/camelrace/green.mov'
import redWebm from '@/assets/videos/camelrace/red.webm'
import redMov from '@/assets/videos/camelrace/red.mov'
import blueWebm from '@/assets/videos/camelrace/blue.webm'
import blueMov from '@/assets/videos/camelrace/blue.mov'
import purpleWebm from '@/assets/videos/camelrace/purple.webm'
import purpleMov from '@/assets/videos/camelrace/purple.mov'
import greenReverseWebm from '@/assets/videos/camelrace/green_reverse.webm'
import greenReverseMov from '@/assets/videos/camelrace/green_reverse.mov'
import redReverseWebm from '@/assets/videos/camelrace/red_reverse.webm'
import redReverseMov from '@/assets/videos/camelrace/red_reverse.mov'
import blueReverseWebm from '@/assets/videos/camelrace/blue_reverse.webm'
import blueReverseMov from '@/assets/videos/camelrace/blue_reverse.mov'
import purpleReverseWebm from '@/assets/videos/camelrace/purple_reverse.webm'
import purpleReverseMov from '@/assets/videos/camelrace/purple_reverse.mov'

export const camelraceAssets = defineStore('camelraceAssets', {
    state: () => ({
        assets: {
            backgroundLoop: {
                url: bgLoopMp4,
                iosUrl: bgLoopMp4,
                blobUrl: null,
            },
            gates: {
                url: gatesWebm,
                iosUrl: gatesMov,
                blobUrl: null,
            },
            green: {
                url: greenWebm,
                iosUrl: greenMov,
                blobUrl: null,
            },
            red: {
                url: redWebm,
                iosUrl: redMov,
                blobUrl: null,
            },
            blue: {
                url: blueWebm,
                iosUrl: blueMov,
                blobUrl: null,
            },
            purple: {
                url: purpleWebm,
                iosUrl: purpleMov,
                blobUrl: null,
            },
            green_reverse: {
                url: greenReverseWebm,
                iosUrl: greenReverseMov,
                blobUrl: null,
            },
            red_reverse: {
                url: redReverseWebm,
                iosUrl: redReverseMov,
                blobUrl: null,
            },
            blue_reverse: {
                url: blueReverseWebm,
                iosUrl: blueReverseMov,
                blobUrl: null,
            },
            purple_reverse: {
                url: purpleReverseWebm,
                iosUrl: purpleReverseMov,
                blobUrl: null,
            },
        },
        progress: 0,
    }),
    getters: {
        isReady() {
            return (
                Object.values(this.assets).every((asset) => asset.blobUrl !== null) &&
                this.progress === 100
            )
        },
    },
    actions: {
        // Check if on iOS
        isIOS() {
            // 1. Check the standard iOS user agents (catches iPhones, iPods, and Chrome iOS)
            const isIOSUserAgent = /(iPad|iPhone|iPod)/i.test(navigator.userAgent)

            // 2. Check for "Desktop" Safari on iPads by detecting Mac formatting + touch capability
            const isMacTouch = navigator.userAgent.includes('Mac') && 'ontouchend' in document

            return isIOSUserAgent || isMacTouch
        },

        // Get the source URL for an asset
        getSourceUrl(assetKey) {
            const asset = this.assets[assetKey]
            if (!asset) return null
            return this.isIOS() ? asset.iosUrl : asset.url
        },

        getBlobUrl(assetKey) {
            const asset = this.assets[assetKey]
            if (!asset) return null
            if (!asset.blobUrl) return null
            // Android WebView needs #t=0.1 to render a first-frame poster.
            // iOS WebKit breaks on media fragments with blob: URLs, so skip there.
            return this.isIOS() ? asset.blobUrl : asset.blobUrl + '#t=0.1'
        },

        getEncoding(assetKey) {
            const asset = this.assets[assetKey]
            if (!asset) return 'video/mp4'
            let userAgent = navigator.userAgent || navigator.vendor || window.opera

            if (assetKey === 'backgroundLoop') {
                return 'video/mp4'
            }

            if (/windows phone/i.test(userAgent)) {
                return 'video/webm'
            }

            if (/android/i.test(userAgent)) {
                return 'video/webm'
            }


            if (this.isIOS() && !window.MSStream) {
                return 'video/mp4; codecs="hvc1"'
            }

            return 'video/webm'
        },

        async preloadAssets() {
            // fetch() does not work on file:// (WKWebView). Use URLs directly.
            if (window.location.protocol === 'file:') {
                for (const key of Object.keys(this.assets)) {
                    this.assets[key].blobUrl = this.getSourceUrl(key)
                }
                this.progress = 100
                return
            }

            const assetKeys = Object.keys(this.assets)
            const totalAssets = assetKeys.length
            const assetProgress = {}

            // Initialize progress tracking for each asset
            assetKeys.forEach((key) => {
                assetProgress[key] = 0
            })

            this.progress = 0

            const promises = assetKeys.map(async (key) => {
                try {
                    const sourceUrl = this.getSourceUrl(key)
                    const encoding = this.getEncoding(key)

                    if (!sourceUrl) {
                        throw new Error(`No source URL found for asset ${key}`)
                    }

                    // Fetch with progress tracking
                    const response = await fetch(sourceUrl)
                    if (!response.ok) {
                        throw new Error(`Failed to fetch ${key}: ${response.statusText}`)
                    }

                    const contentLength = response.headers.get('Content-Length')
                    const total = contentLength ? parseInt(contentLength, 10) : 0
                    let loaded = 0

                    const reader = response.body.getReader()
                    const chunks = []

                    while (true) {
                        const { done, value } = await reader.read()

                        if (done) break

                        chunks.push(value)
                        loaded += value.length

                        // Update progress for this specific asset
                        if (total > 0) {
                            assetProgress[key] = Math.round((loaded / total) * 100)
                        }

                        // Calculate overall progress
                        const totalProgress = Object.values(assetProgress).reduce(
                            (sum, progress) => sum + progress,
                            0,
                        )
                        this.progress = Math.round(totalProgress / totalAssets)
                    }

                    // Create blob from chunks with proper encoding
                    const blob = new Blob(chunks, { type: encoding })
                    const blobUrl = URL.createObjectURL(blob)

                    // Store the blob URL and encoding
                    this.assets[key].blobUrl = blobUrl
                    this.assets[key].encoding = encoding

                    // Ensure this asset is marked as 100% complete
                    assetProgress[key] = 100
                    const totalProgress = Object.values(assetProgress).reduce(
                        (sum, progress) => sum + progress,
                        0,
                    )
                    this.progress = Math.round(totalProgress / totalAssets)
                } catch (error) {
                    console.error(`Error preloading asset ${key}:`, error)
                    // Mark as complete even on error to prevent hanging
                    assetProgress[key] = 100
                    const totalProgress = Object.values(assetProgress).reduce(
                        (sum, progress) => sum + progress,
                        0,
                    )
                    this.progress = Math.round(totalProgress / totalAssets)
                }
            })

            await Promise.all(promises)
        },

        // Clean up blob URLs when no longer needed
        cleanupBlobUrls() {
            Object.values(this.assets).forEach((asset) => {
                if (asset.blobUrl && asset.blobUrl.startsWith('blob:')) {
                    URL.revokeObjectURL(asset.blobUrl)
                    asset.blobUrl = null
                }
            })
        },
    },
})
